import { QueryClient, useQuery, UseQueryResult } from '@tanstack/react-query';
import { SpendingSumModel } from 'src/types/spending-sum.model';
import SpendingService from 'src/services/spending.service';
import {
    GetSpendingHistoryArgs,
    GetSpendingSumForCategoryArgs,
    GetSpendingSumsArgs,
    SpendingHistory,
    SpendingTimePeriod
} from 'src/types/spending';
import DateUtils from 'src/utils/date';
import { invalidateTransactions } from 'src/hooks/queries/transactions';

const spendingSumsQueryKey: string = 'spendingSums';

export const useSpendingSumForCategory = ({
    id,
    startDate,
    endDate,
    enabled = true
}: GetSpendingSumForCategoryArgs): UseQueryResult<SpendingSumModel> => {
    return useQuery<SpendingSumModel>({
        queryKey: [spendingSumsQueryKey, id, startDate, endDate],
        queryFn: async () => await SpendingService.getSpendingSumForCategory(id, startDate, endDate),
        enabled
    });
};
export const useSpendingSums = (args: GetSpendingSumsArgs): UseQueryResult<SpendingSumModel[]> => {
    const { startDate, endDate } = args;
    return useQuery<SpendingSumModel[]>({
        queryKey: [spendingSumsQueryKey, startDate, endDate],
        queryFn: async () => await SpendingService.getSpendingSums(startDate, endDate)
    });
};

const spendingHistoryQueryKey = 'spendingHistory';

export function useSpendingHistory({
    id = undefined,
    timePeriod = SpendingTimePeriod.MONTH,
    startDate = DateUtils.startDateOfThisMonth(),
    endDate = DateUtils.startDateOfNextMonth(),
    cumulative = false,
    includeTransfers = true,
    enabled = true
}: GetSpendingHistoryArgs): UseQueryResult<SpendingHistory[]> {
    return useQuery<SpendingHistory[]>({
        queryKey: [spendingHistoryQueryKey, id, timePeriod, startDate, endDate, cumulative, includeTransfers],
        queryFn: async () =>
            await SpendingService.getSpendingHistory(id, timePeriod, startDate, endDate, cumulative, includeTransfers),
        enabled
    });
}

export const invalidateSpendingSums = async (queryClient: QueryClient): Promise<void> => {
    await queryClient.invalidateQueries([spendingSumsQueryKey]);
};

export const invalidateSpendingHistory = async (queryClient: QueryClient): Promise<void> => {
    await queryClient.invalidateQueries([spendingHistoryQueryKey]);
};

export const invalidateTransactionsSpending = async (
    queryClient: QueryClient,
    transactionId?: number
): Promise<void> => {
    await invalidateTransactions(queryClient, transactionId);
    await invalidateSpendingSums(queryClient);
    await invalidateSpendingHistory(queryClient);
};
